import React, { useCallback, useEffect } from 'react'

import { Controller, useWatch } from 'react-hook-form'

import {
  Button,
  TextInput,
  Icon,
  VisibilityOn,
  VisibilityOff,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, string } from 'prop-types'

import SubscriptionEvents from 'Pods/Shared/Constants/subscriptionEvents'
import useTracker from 'Pods/Shared/Hooks/useTracker'
import I18n from 'Pods/Shared/I18n'
import FacebookIcon from 'Pods/Shared/Icons/FacebookIcon'
import GoogleIcon from 'Pods/Shared/Icons/GoogleIcon'
import { useLogin } from 'Pods/Shared/User/Hooks/useLogin'

import MTCaptcha from 'Utils/MTCaptcha'
import { NOOP } from 'Utils/stableDefaults'

const CAPTCHA_ID = 'subscription-login-captcha'

function LoginStep({
  locale = 'en',
  email: initialEmail = '',
  showExistingAccountNotice = false,
  onLoginSuccess = NOOP,
}) {
  const t = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  const { trackEvent } = useTracker()

  useEffect(() => {
    trackEvent(SubscriptionEvents.GUEST_LOGIN_STEP_VIEWED)
  }, [])

  const handleLoginSuccess = useCallback((response) => {
    trackEvent(SubscriptionEvents.GUEST_LOGIN_COMPLETED)
    onLoginSuccess(response)
  }, [onLoginSuccess, trackEvent])

  const {
    token,
    control,
    errors,
    touchedFields,
    showPassword,
    twoFASecondState,
    twoFaCode,
    loginCaptchaEnabled,
    handleSubmit,
    onFormSubmit,
    onTwoFaSubmit,
    onTwoFaCodeChange,
    onShowPasswordToggle,
  } = useLogin({
    captchaId: CAPTCHA_ID,
    locale,
    onSuccess: handleLoginSuccess,
    defaultEmail: initialEmail,
  })

  const watchedEmail = useWatch({ control, name: 'email', defaultValue: initialEmail })
  const watchedPassword = useWatch({ control, name: 'password', defaultValue: '' })
  const isSubmitDisabled = !watchedEmail?.trim() || !watchedPassword

  function handleSocialLogin(e) {
    e.preventDefault()
    e.target.submit()
  }

  const originParam = encodeURIComponent(window.location.href)

  if (twoFASecondState) {
    return (
      <div className="login-step">
        <p className="login-step__title">
          {t('login.two_fa.title')}
        </p>
        <p className="login-step__subtitle">
          {t('login.two_fa.description')}
        </p>
        <form onSubmit={onTwoFaSubmit}>
          <TextInput
            name="twoFaCode"
            id="twoFaCode"
            wrapperClassName="auth-tab__field"
            isPilot
            isRequired
            value={twoFaCode}
            handleChange={onTwoFaCodeChange}
          />
          <Button expand actionType="submit">
            {t('login.two_fa.verify_button')}
          </Button>
        </form>
      </div>
    )
  }

  return (
    <div className="login-step">
      {showExistingAccountNotice && (
        <div className="login-step__info-banner">
          <p className="login-step__info-banner-title cgt-body-md-semibold">
            {t('subscription_checkout.login_step.info_title')}
          </p>
          <p className="login-step__info-banner-subtitle cgt-body-base">
            {t('subscription_checkout.login_step.info_subtitle')}
          </p>
        </div>
      )}

      <h1 className="login-step__title cgt-heading-300">
        {t('subscription_checkout.login_step.title')}
      </h1>
      <p className="login-step__subtitle cgt-body-base">
        {t('subscription_checkout.login_step.subtitle')}
      </p>

      <div className="login-step__social-buttons">
        <form
          className="login-step__social-form"
          action={`/users/auth/google_oauth2?origin=${originParam}`}
          method="post"
          onSubmit={handleSocialLogin}
        >
          <Button
            expand
            size="large"
            icon={<GoogleIcon />}
            type="primary"
            variant="only-icon"
            customClass="cgt-button--google-plus justify-content-center"
          />
          {token && <input type="hidden" name="authenticity_token" value={token} />}
        </form>
        <form
          className="login-step__social-form"
          action={`/users/auth/facebook?origin=${originParam}`}
          method="post"
          onSubmit={handleSocialLogin}
        >
          <Button
            expand
            size="large"
            icon={<FacebookIcon />}
            type="primary"
            variant="only-icon"
            customClass="cgt-button--facebook justify-content-center"
          />
          {token && <input type="hidden" name="authenticity_token" value={token} />}
        </form>
      </div>

      <div className="login-step__divider">
        <span className="cgt-text-medium cgt-legacy-color-grey-50">
          {t('subscription_checkout.email_step.divider')}
        </span>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('login.validation.email_username_required'),
          }}
          render={({ field }) => (
            <TextInput
              label={<span className="cgt-caption-semibold">{t('subscription_checkout.login_step.email_label')}</span>}
              id="login-email"
              name="email"
              isWarning={touchedFields.email && !!errors.email}
              helpText={touchedFields.email ? errors.email?.message : ''}
              value={field.value}
              wrapperClassName="auth-tab__field"
              handleChange={field.onChange}
              onBlur={field.onBlur}
              isPilot
              isRequired
            />
          )}
        />
        <Controller
          name="email_confirmation"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t('login.email_confirmation_label')}
              id="login-email-confirmation"
              name="email_confirmation"
              value={field.value}
              wrapperClassName="auth-tab__field hidden"
              handleChange={field.onChange}
              autoComplete="off"
              isPilot
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          rules={{
            required: t('login.validation.password_required'),
          }}
          render={({ field }) => (
            <TextInput
              label={
                <span className="cgt-caption-semibold">{t('subscription_checkout.login_step.password_label')}</span>
              }
              id="login-password"
              name="password"
              inputType={showPassword ? 'text' : 'password'}
              isWarning={touchedFields.password && !!errors.password}
              helpText={touchedFields.password ? errors.password?.message : ''}
              value={field.value}
              wrapperClassName="auth-tab__field"
              handleChange={field.onChange}
              onBlur={field.onBlur}
              isPilot
              isRequired
              trailIcon={(
                <Icon
                  svgComponent={showPassword ? VisibilityOff : VisibilityOn}
                  color="dark"
                  withHover
                  onClick={onShowPasswordToggle}
                  aria-label={t(showPassword
                    ? 'subscription_checkout.login_step.hide_password'
                    : 'subscription_checkout.login_step.show_password')}
                />
              )}
            />
          )}
        />
        {loginCaptchaEnabled && <MTCaptcha captchaId={CAPTCHA_ID} />}
        <Button
          expand
          size="large"
          actionType="submit"
          disabled={isSubmitDisabled}
        >
          {t('subscription_checkout.login_step.login_button')}
        </Button>
      </form>

      <p className="login-step__forgot-password">
        <a href="/users/password/new" className="cgt-legacy-link">
          {t('subscription_checkout.login_step.forgot_password')}
        </a>
      </p>
    </div>
  )
}

LoginStep.propTypes = {
  locale: string,
  email: string,
  showExistingAccountNotice: bool,
  onLoginSuccess: func,
}

export default LoginStep
